import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { formatDateShort } from "../../misc/osloDate";
import type { DriftScan } from "./buildRows";

// «Vis funn» på Driftsradar-raden (fiks 5): alle funn med avkrysning, «Velg
// alle» og «Lag oppgaver for valgte (n)». Funn som allerede har en åpen
// oppgave, står avkrysset og låst med «Oppgave finnes». Mellomrom krysser av
// (vanlig avkrysningsboks).

export const DriftFindings = ({
  scan,
  existing,
  preselect,
  onCreate,
  onClose,
}: {
  scan: DriftScan;
  /** Funn med åpen oppgave. */
  existing: Set<string>;
  /** «Lag oppgaver» åpner med alle ledige funn valgt. */
  preselect: boolean;
  onCreate: (findings: string[]) => Promise<void>;
  onClose: () => void;
}) => {
  const free = scan.findings.filter((f) => !existing.has(f));
  const [chosen, setChosen] = useState<Set<string>>(
    () => new Set(preselect ? free : []),
  );
  const [saving, setSaving] = useState(false);
  const allChosen = free.length > 0 && free.every((f) => chosen.has(f));
  const toggle = (finding: string) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(finding)) next.delete(finding);
      else next.add(finding);
      return next;
    });

  return (
    <div
      className="flex flex-col gap-3 border-t bg-muted/20 px-4 py-4 sm:pl-11"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
        <span>Lett skann {formatDateShort(scan.scannedAt)}</span>
        {free.length > 1 && (
          <button
            type="button"
            className="underline-offset-4 hover:text-foreground hover:underline"
            onClick={() => setChosen(new Set(allChosen ? [] : free))}
          >
            {allChosen ? "Velg ingen" : "Velg alle"}
          </button>
        )}
      </div>
      <ul className="flex flex-col gap-2" aria-label="Funn på nettstedet">
        {scan.findings.map((finding, i) => {
          const has = existing.has(finding);
          const id = `drift-${scan.companyId}-${i}`;
          return (
            <li key={finding} className="flex items-center gap-2.5 text-[13px]">
              <Checkbox
                id={id}
                checked={has || chosen.has(finding)}
                disabled={has}
                onCheckedChange={() => toggle(finding)}
              />
              <label
                htmlFor={id}
                className={has ? "text-muted-foreground" : undefined}
              >
                {finding}
              </label>
              {has && (
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  Oppgave finnes
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-end gap-3">
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Lukk
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={chosen.size === 0 || saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onCreate(free.filter((f) => chosen.has(f)));
              setChosen(new Set());
            } finally {
              setSaving(false);
            }
          }}
        >
          Lag oppgaver for valgte ({chosen.size})
        </Button>
      </div>
    </div>
  );
};
